/* eslint-disable @typescript-eslint/no-explicit-any */
import { QUERY_CLIENT_STALE_TIME } from "@dw/constants";
import { Dayjs } from '@dw/dayjs'

export type THttpMethod = "GET" | "POST" | "PUT" | "DELETE";
export type TColor =
  | "primary"
  | "secondary"
  | "tertiary"
  | "error"
  | "success"
  | "warning"
  | "info"
  | "light"
  | "dark";
  
export type TDateFormat = "datetime" | "date" | "time";
export type TDataDisplayType = TDateFormat | "currency" | "number";

export enum QueryKeys {
  AppSettings = "AppSettingsQueryKey",
  Self = "SelfQueryKey",
  DanceSchools = "DanceSchoolsQueryKey",
  DanceSchoolMetrics = "DanceSchoolMetricsQueryKey",
  DanceSchoolCommunicationSettings = "DanceSchoolCommunicationSettingsQueryKey",
  DanceSchoolCommunicationMessages = "DanceSchoolCommunicationMessagesQueryKey",
  DanceSchoolUsers = "DanceSchoolUsersQueryKey",
  Locations = "LocationsQueryKey",
  Studios = "StudiosQueryKey",
  DanceSchoolDancerAccounts = "DanceSchoolDancerAccountsQueryKey",
  Students = "StudentsQueryKey",
  DanceClasses = "DanceClassesQueryKey",
  XeroChartOfAccounts = "XeroChartOfAccountsQueryKey",
  DanceSchoolAccountInvoices = "DanceSchoolAccountInvoicesQueryKey",
  DanceClassTrials = "DanceClassTrialsQueryKey",
  DanceClassAttendances = "DanceClassAttendancesQueryKey",
  DanceClassSchedulePlans = "DanceClassSchedulePlansQueryKey",
  DanceSchoolTemplatedEmailConfigs = "DanceSchoolTemplatedEmailConfigsQueryKey",
  SyncIssues = "SyncIssuesQueryKey",
  TeacherNotes = "TeacherNotesQueryKey",
  MyTeacherNotes = "MyTeacherNotesQueryKey",
  Notices = "NoticesQueryKey",
  NoticeThemes = "NoticeThemesQueryKey",
  /** A school's internal staff notes, keyed by the kind of record they are written against. */
  InternalNotes = "InternalNotesQueryKey",
  /** The files a studio has shared against one dance class. */
  DanceClassFiles = "DanceClassFilesQueryKey",
  Exams = "ExamsQueryKey",
  Examiner = "ExaminerQueryKey",
  PublicInvoiceView = "PublicInvoiceViewQueryKey",
  LocalStorage = "LocalStorageQueryKey",
  SessionStorage = "SessionStorageQueryKey",
}

export interface IAuditBase {
  createdTimestamp: Dayjs;
}

export interface IQuerySettings<TResponse = any, TInput = any> {
  enabled?: boolean;
  onSuccess?: (response: TResponse, input?: TInput) => void;
  onError?: (error: HttpClientError, input?: TInput) => void;
  onSettled?: () => void;
  staleTime?: number;
  refetchInterval?: number | false;
  /** Needed alongside `refetchInterval` — the global default is `false`, so a polled query that
   * omits this stays stale while the user is on another tab and only catches up a full interval
   * after they return. */
  refetchOnWindowFocus?: boolean;
  keepPreviousData?: boolean;
  retry?: boolean | number | ((failureCount: number, error: any) => boolean);
  retryDelay?: number;

  autoShowErrorMessages?: boolean;
  autoLogOutOnAuthError?: boolean;
  silent?: boolean;
}
export function getQuerySettings<TResponse = any, TInput = any>(
  settings?: IQuerySettings<TResponse, TInput>
): IQuerySettings<TResponse, TInput> {
  return {
    enabled: true,
    staleTime: QUERY_CLIENT_STALE_TIME,
    refetchInterval: false,
    autoShowErrorMessages: true,
    autoLogOutOnAuthError: true,
    retryDelay: 1000,
    onError: (error) => {
      // Swallow error by default. Don't want each non 200 to raise alarms in NR
      console.warn(error);
    },
    ...(settings ?? {}),
    retry: !!settings?.retry && typeof settings.retry === 'number' ? (failureCount: number) => { return failureCount <= (settings.retry as number) } : (settings?.retry ?? false),
  };
}

export interface IReducerAction<T> {
  type: T;
  payload?: any;
}

export enum FEATURE_FLAG {}

export interface IAppSettings {
  apiBaseUrl: string;
  blobBaseUrl: string;
  deepLinkBaseDomain: string;
  environment?: "local" | "dev" | "prod";
  buildVersion?: string;
  googleApiKey: string;
  featureFlags: Partial<Record<FEATURE_FLAG, boolean>>;
  mobileAppSchemes: {
    android: string;
    ios: string;
    iosAppId: string;
  }
}

export interface IMenuItem {
  value?: string;
  icon?: React.ReactNode;
  text?: string;
  content?: React.ReactNode;
  isDivider?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  routerLink?: string;
  data?: any;
  color?: TColor;
}

export interface IDefaultResponseMessage {
  message?: string;
  boolean?: boolean;
}

export interface IAddress {
  fullAddress: string;
  street: string;
  suburb: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  countryCode: string;
  addressId: string;
  geoPoint?: IGeoPoint | null;
}

export interface IGeoPoint {
  latitude: number;
  longitude: number;
}

export interface IPhoneNumber {
  countryDialingCode: string;
  localNumber: string;
  internationalNumber: string;
}

export interface IDateOnlyRange {
  startDate: Dayjs;
  endDate: Dayjs;
}
export interface IDateOnlyRangeRequest {
  startDate: string;
  endDate: string;
}

export enum FEE_TAX_SETTING {
  NO_TAX = "NoTax",
  TAX_INCLUDED = "TaxIncluded",
  TAX_EXCLUDED = "TaxExcluded"
}

export interface IRedirectUrl {
  url: string;
}

export interface IBlobFileMetadata {
  key: string;
  fileName: string;
  contentType: string;
  fileSizeInBytes: number;
  metaData: Record<string, string>;
}

interface IHttpErrorResponse {
  requestUrl: string;
  requestMethod: THttpMethod;
  statusCode: number;
  errorMessages: string[];
  rawErrors?: any;
}
export class HttpClientError extends Error {
  public statusCode: number;
  public firstErrorMessage: string;
  public errorMessages: string[];

  public errorResponse: IHttpErrorResponse;

  constructor(errorResponse: IHttpErrorResponse) {
    super(`[${errorResponse.statusCode}] error while performing [${errorResponse.requestMethod} ${errorResponse.requestUrl}]`);

    Object.setPrototypeOf(this, HttpClientError.prototype);

    this.errorResponse = errorResponse;
    this.statusCode = errorResponse.statusCode;
    this.firstErrorMessage = errorResponse.errorMessages[0];
    this.errorMessages = errorResponse.errorMessages;
  }
}